'use client'

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Check } from 'lucide-react';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { getFirestore, collection, addDoc, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db, firebaseAuth as auth } from '@/libs/firebase/config';
import { signInWithGoogle } from '@/libs/firebase/auth';
import { usePremiumStatus } from '@/hooks/use-premium-status';
import StripeCheckoutSkeleton from '@/components/skeletons/StripeCheckoutSkeleton';
import { Button } from '@/components/ui/Button';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/config/i18n';

const startStripeCheckout = async (userId, setLoading, locale) => {
  console.log('startStripeCheckout called');
  try {
    console.log('Stripe price ID:', process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_24H_DECEMBER_PROMO);
    const normalizedLocale = SUPPORTED_LOCALES.includes(locale.toLowerCase()) ? locale.toLowerCase() : DEFAULT_LOCALE;
    const checkoutSessionRef = collection(db, 'customers', userId, 'checkout_sessions');
    const docRef = await addDoc(checkoutSessionRef, {
      mode: 'payment',
      price: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_24H_DECEMBER_PROMO,
      success_url: `${window.location.origin}/${normalizedLocale}/temple`,
      cancel_url: `${window.location.origin}/${normalizedLocale}`,
      metadata: {
        productType: 'premium_access_24h',
        priceId: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_24H_DECEMBER_PROMO
      }
    });

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        const data = snap.data();
        const { error, url } = data || {};

        if (error) {
          console.error(`Stripe Checkout Error: ${error.message}`);
          alert(`An error occurred: ${error.message}`);
          unsubscribe();
          setLoading(false);
        }

        if (url) {
          console.log('Redirecting to Stripe Checkout:', url);
          unsubscribe();
          window.location.assign(url);
        }
      },
      (err) => {
        console.error('Firestore snapshot listener error:', err);
        alert('Failed to listen for checkout session updates. Please try again.');
        unsubscribe();
        setLoading(false);
      }
    );
  } catch (error) {
    console.error('Error creating checkout session document:', error);
    alert('Failed to initiate payment. Please try again.');
    setLoading(false);
  }
};

const PricingCard = () => {
  const t = useTranslations('common.pricingCard');
  const locale = useLocale();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { isPremium, loading: premiumLoading } = usePremiumStatus();

  const handlePurchase = async () => {
    setLoading(true);
    let currentUser = auth.currentUser;
    try {
      if (!currentUser) {
        console.log('User not logged in. Initiating Google Sign-In.');
        try {
          const userFromSignIn = await signInWithGoogle();

          if (userFromSignIn) {
            currentUser = userFromSignIn;
            console.log('Sign-in successful via PricingCard for:', currentUser.email);
          } else {
            console.log('Google sign-in redirect initiated from PricingCard. Purchase will continue after sign-in.');
            setLoading(false);
            return;
          }

          if (!currentUser) {
            alert('Authentication failed. Please try again.');
            setLoading(false);
            return;
          }
        } catch (signInError) {
          if (signInError.code === 'auth/popup-closed-by-user') {
            alert('Sign-in process was cancelled.');
          } else if (signInError.code === 'auth/cancelled-popup-request') {
            alert('Sign-in popup request was cancelled.');
          } else if (signInError.code === 'auth/redirect-cancelled' || signInError.message.includes('redirect')) {
            alert('Sign-in redirect operation was cancelled or failed. Please try again.');
          } else {
            console.error('Error during Google sign-in from PricingCard:', signInError);
            alert('An error occurred during sign-in. Please try again.');
          }
          setLoading(false);
          return;
        }
      } else {
        console.log('User already logged in:', currentUser.email);
      }

      if (isPremium) {
        router.push(`/${locale}/temple`);
        setLoading(false);
        return;
      }
      await startStripeCheckout(currentUser.uid, setLoading, locale);
    } catch (error) {
      console.error('Error during purchase process (after sign-in check):', error);
      alert('An error occurred during the purchase process. Please try again.');
      setLoading(false);
    }
  };

  const price = t.raw('price');
  const bullets = t.raw('bullets');

  return (
    <>
      <div className="bg-[#050316] p-7 rounded-2xl shadow-[0_0_40px_rgba(164,93,232,0.25)] max-w-md w-full border border-[#6A3683] mx-auto relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
          <div className="absolute top-0 right-0 w-60 h-60 bg-[#A45DE8] rounded-full blur-[80px] -translate-y-1/2 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-[#D4B6FF] rounded-full blur-[80px] translate-y-1/2 -translate-x-1/4"></div>
        </div>

        <div className="relative z-10">
          <div className="absolute top-0 right-2 z-20">
            <Image
              src="/images/oracle-luna.png"
              alt="Oracle Luna"
              width={96}
              height={96}
              className="rounded-full shadow-[0_0_15px_rgba(164,93,232,0.3)]"
            />
          </div>

          <div className="flex justify-between items-center mb-3 pr-32">
            <h2 className="text-xl font-serif text-purple-400 tracking-wide font-marcellus">{t('title')}</h2>
          </div>

          <div className="mb-4 flex items-end">
            <span className="text-5xl font-bold text-[#D4B6FF] font-sans">{price.amount}</span>
            <span className="text-sm text-[#FBFBFE]/70 ml-1 mb-1"> {price.suffix}</span>
          </div>

          <p className="text-[#FBFBFE] mb-5 font-light text-left">{t('description')}</p>

          <ul className="space-y-3 mb-5">
            {Array.isArray(bullets) && bullets.map((item, index) => (
              <li key={index} className="flex items-center">
                <div
                  className={`mr-3 w-5 h-5 flex items-center justify-center rounded-full text-[#FBFBFE] ${
                    index === 0 ? 'bg-[#A45DE8]' : 'bg-[#6A3683]'
                  }`}
                >
                  <Check className="h-3 w-3" />
                </div>
                <span className={`text-[#FBFBFE]${index === 0 ? ' text-[#D4B6FF] font-semibold' : ''}`}>
                  {item}
                </span>
              </li>
            ))}
          </ul>

          <Button
            type="button"
            onClick={handlePurchase}
            loading={loading}
            disabled={loading}
            variant="primary"
            className="w-full py-3 px-6 animate-pulse-subtle"
          >
            {t('button')}
          </Button>
        </div>
      </div>

      {loading && <StripeCheckoutSkeleton />}
    </>
  );
};

export default PricingCard; 